import {
  FormControl,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Tooltip,
} from '@chakra-ui/react';
import type { ContainerProps, SliderProps } from '@chakra-ui/react';
import { useState } from 'react';
import { useController } from 'react-hook-form';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';
import LabelForm from './LabelForm';

type SliderValue = 0 | 10 | 20 | 30 | 40 | 50 | 60 | 70 | 80 | 90 | 100;

type SliderField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<SliderProps, 'onChange' | 'onBlur'> & {
  control?: Control<TFieldValues>;
  name: TName;
  label?: string;
  onChange?: (value?: SliderValue) => void;
  onBlur?: () => void;
  showTooltip?: boolean;
  containerStyles?: ContainerProps;
};

const SliderField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  label,
  name,
  onBlur,
  onChange,
  showTooltip,
  containerStyles,
  ...rest
}: SliderField<TFieldValues, TName>) => {
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);

  const {
    field: { onChange: onFieldChange, onBlur: onFieldBlur, value, ...restFieldControls },
  } = useController({
    name,
    control,
  });

  const handleChange = (value: SliderValue) => {
    onFieldChange(value);
    onChange?.(value);
  };

  const handleBlur = () => {
    onFieldBlur();
    onBlur?.();
  };

  return (
    <FormControl {...containerStyles}>
      {label && <LabelForm label={label} />}

      <Slider
        colorScheme='secondary'
        id={name}
        max={100}
        min={0}
        step={10}
        value={value}
        onBlur={handleBlur}
        onChange={handleChange}
        onMouseEnter={() => showTooltip ?? setIsTooltipVisible(true)}
        onMouseLeave={() => showTooltip ?? setIsTooltipVisible(false)}
        {...rest}
        {...restFieldControls}
      >
        <SliderTrack>
          <SliderFilledTrack />
        </SliderTrack>
        <Tooltip
          bg='primary.base'
          borderRadius='full'
          color='typography.pure'
          isOpen={isTooltipVisible}
          label={value}
          placement='top'
        >
          <SliderThumb />
        </Tooltip>
      </Slider>
    </FormControl>
  );
};

export default SliderField;
